import {
  MACROFACTOR_FILES,
  SOURCE as MACROFACTOR,
  kindOf,
  readMacroFactor,
  type MacroFactorFile,
  type MacroFactorImport,
  type Workbook,
} from '@gymcloud/macrofactor'
import { readWorkbook } from '@gymcloud/macrofactor/xlsx'
import { Link, useBlocker } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { EXPORT_FORMATS, EXPORT_GROUPS, type ExportFormat, type ExportGroup } from '~/api/endpoints'
import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { DialogLink } from '~/components/dialog-link'
import {
  CheckIcon,
  ChevronRightIcon,
  CloseIcon,
  DownloadIcon,
  UploadIcon,
} from '~/components/icons'
import { useToast } from '~/components/toast-context'
import { useApi } from '~/data/data-context'
import { pagesOf, summaryOf } from '~/data/import-reading'
import { reloadNutrition } from '~/data/nutrition-store'
import { formatDayLabel, dayStart } from '~/lib/dates'
import { withoutDialog } from '~/lib/dialogs'
import { saveBlob } from '~/lib/download-video'

/**
 * The Data section of settings and the two processes it leads to, each morphed into the
 * corner card a step at a time: taking the account's data out, and bringing another app's in.
 * See `specs/features/data.spec.md`.
 */

const GROUP_TITLES: Record<ExportGroup, string> = {
  training: 'Training',
  body: 'Body',
  nutrition: 'Nutrition',
  account: 'Account',
}

const GROUP_HOLDS: Record<ExportGroup, string> = {
  training: 'Sets, workouts, planned sets, templates and their versions, runs and listings.',
  body: 'Weigh-ins, progress photos and daily metrics.',
  nutrition: 'Entries, days, your own foods, favourites and the goal history.',
  account: 'The account and its settings, posts, connections and feedback you sent.',
}

const FORMAT_TITLES: Record<ExportFormat, string> = { json: 'JSON', csv: 'CSV' }

const FORMAT_HOLDS: Record<ExportFormat, string> = {
  json: 'One file holding every piece by name.',
  csv: 'One file per piece, gathered in a zip.',
}

const primary =
  'rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink disabled:opacity-50'

export function DataSection() {
  return (
    <nav className="pb-1">
      <DialogLink dialog="export" className="menu-row">
        <DownloadIcon aria-hidden className="size-7 text-ink-muted" />
        <span className="min-w-0 flex-1">
          <span className="block font-medium">Export your data</span>
          <span className="block text-sm text-ink-muted">
            Everything you have logged, as a file of your own.
          </span>
        </span>
        <ChevronRightIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
      </DialogLink>
      <DialogLink dialog="import" className="menu-row">
        <UploadIcon aria-hidden className="size-7 text-ink-muted" />
        <span className="min-w-0 flex-1">
          <span className="block font-medium">Import from another app</span>
          <span className="block text-sm text-ink-muted">
            Bring your diary across from MacroFactor.
          </span>
        </span>
        <ChevronRightIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
      </DialogLink>
    </nav>
  )
}

function StepLine({ at, of, title }: { at: number; of: number; title: string }) {
  return (
    <p className="px-3 pb-2 text-sm text-ink-muted">
      {`Step ${String(at)} of ${String(of)} · ${title}`}
    </p>
  )
}

// ── Exporting ────────────────────────────────────────────────────────────────────────────

type ExportStep = 'groups' | 'format' | 'download'
const EXPORT_STEPS: ExportStep[] = ['groups', 'format', 'download']
const EXPORT_STEP_TITLES: Record<ExportStep, string> = {
  groups: 'What to export',
  format: 'Which format',
  download: 'Your file',
}

export function ExportPanel({ onBack }: { onBack: () => void }) {
  const api = useApi()
  const toast = useToast()
  const [step, setStep] = useState<ExportStep>('groups')
  const [groups, setGroups] = useState<ExportGroup[]>([...EXPORT_GROUPS])
  const [format, setFormat] = useState<ExportFormat>('json')
  const [status, setStatus] = useState<'idle' | 'working' | 'failed'>('idle')
  const exportShown = useBeat(status === 'working')

  const at = EXPORT_STEPS.indexOf(step)

  function backward(): void {
    if (status === 'working') {
      return
    }
    const previous = EXPORT_STEPS[at - 1]
    // The last step's back leaves in one press: there is nothing to go back to but the choices.
    if (previous === undefined || step === 'download') {
      onBack()
    } else {
      setStep(previous)
      setStatus('idle')
    }
  }

  async function download(): Promise<void> {
    if (status === 'working') {
      return
    }
    setStatus('working')
    try {
      const file = await api.exportData(groups, format)
      const day = new Date().toISOString().slice(0, 10)
      const name = `mixedgrip-export-${day}.${format === 'json' ? 'json' : 'zip'}`
      saveBlob(file, name)
      // Saved, so the process is over: it leaves at once and says so over the page it lands on.
      toast(`Saved as ${name}`)
      onBack()
    } catch {
      setStatus('failed')
    }
  }

  return (
    <>
      <PanelHeader title="Export your data" onBack={backward} />
      <StepLine at={at + 1} of={EXPORT_STEPS.length} title={EXPORT_STEP_TITLES[step]} />
      <div className="w-88 px-2 pb-2">
        {step === 'groups' && (
          <>
            <fieldset>
              <legend className="sr-only">Groups to export</legend>
              {EXPORT_GROUPS.map((group) => (
                <label
                  key={group}
                  className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-hover"
                >
                  <input
                    type="checkbox"
                    aria-label={GROUP_TITLES[group]}
                    className="mt-0.5 h-5 w-5 accent-accent"
                    checked={groups.includes(group)}
                    onChange={(event) => {
                      setGroups((held) =>
                        event.target.checked
                          ? EXPORT_GROUPS.filter((each) => each === group || held.includes(each))
                          : held.filter((each) => each !== group),
                      )
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{GROUP_TITLES[group]}</span>
                    <span className="block text-sm text-ink-muted">{GROUP_HOLDS[group]}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="flex justify-end px-3 pt-2 pb-1">
              <button
                type="button"
                className={primary}
                disabled={groups.length === 0}
                onClick={() => {
                  setStep('format')
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
        {step === 'format' && (
          <>
            <fieldset>
              <legend className="sr-only">Format</legend>
              {EXPORT_FORMATS.map((each) => (
                <label
                  key={each}
                  className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-hover"
                >
                  <input
                    type="radio"
                    name="export-format"
                    aria-label={FORMAT_TITLES[each]}
                    className="mt-0.5 h-5 w-5 accent-accent"
                    checked={format === each}
                    onChange={() => {
                      setFormat(each)
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{FORMAT_TITLES[each]}</span>
                    <span className="block text-sm text-ink-muted">{FORMAT_HOLDS[each]}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="flex justify-end px-3 pt-2 pb-1">
              <button
                type="button"
                className={primary}
                onClick={() => {
                  setStep('download')
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
        {step === 'download' && (
          <div className="space-y-3 px-3 pb-2">
            <p className="text-sm text-ink-muted">
              {`${groups.map((group) => GROUP_TITLES[group]).join(', ')} as ${FORMAT_TITLES[format]}.`}
            </p>
            {status === 'failed' && (
              <p role="alert" className="text-sm text-danger">
                The export could not be had. Try again in a moment.
              </p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                aria-busy={status === 'working' || undefined}
                className={`relative ${primary}`}
                onClick={() => {
                  void download()
                }}
              >
                <Working shown={exportShown}>
                  {status === 'failed' ? 'Try again' : 'Download'}
                </Working>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ── Importing ────────────────────────────────────────────────────────────────────────────

type ImportStep = 'source' | 'how' | 'files' | 'done'
const IMPORT_STEPS: ImportStep[] = ['source', 'how', 'files', 'done']
const IMPORT_STEP_TITLES: Record<ImportStep, string> = {
  source: 'Where from',
  how: 'Taking the export',
  files: 'The files',
  done: 'What came in',
}

/** The sources whose export can be brought in. Reading one is the device's, so this list is too. */
const SOURCES = [MACROFACTOR]

/** The mark a source wears on its card, where it has one. */
const SOURCE_MARKS: Record<string, string> = { macrofactor: '/macrofactor.png' }

/** One step of taking the export out of a source: what to do, and a word more where it helps. */
interface HowToStep {
  action: string
  detail?: string
}

/** How the export is taken out of each source, a step a line. */
const HOW_TO_EXPORT: Record<string, readonly HowToStep[]> = {
  macrofactor: [
    { action: 'Open MacroFactor and go to the More tab.' },
    {
      action: 'Under Data Management, choose Data Export.',
      detail: 'Two files come out of it, and the import needs both.',
    },
    {
      action: 'On the Granular Export tab, select every category and press Export.',
      detail: 'Save the file it offers.',
    },
    {
      action: 'Scroll down to Logs. Under Food Log, choose All Time and press Export Food Log.',
      detail: 'Save that file too.',
    },
  ],
}

type Held = Partial<Record<MacroFactorFile, Workbook>>

interface Done {
  reading: MacroFactorImport
  profileFilled: number
}

/** A file's bytes, read the one way every browser this runs in has. */
function bytesOf(file: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer))
    }
    reader.onerror = () => {
      reject(reader.error ?? new Error('unreadable'))
    }
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Bringing another app's export in: where from, how to take it out of there, then its files
 * — read here, on the device, and held here until the import runs — then what came of it.
 * The process always starts at the beginning, nothing of an earlier one is remembered, and
 * leaving it with files handed over asks first and lets go of them, whichever way out is
 * taken: the heading's back, the card closing, or a link elsewhere.
 */
export function ImportPanel({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const api = useApi()
  const { setSettings } = useAuth()
  const [source, setSource] = useState<string>(SOURCES[0]?.id ?? '')
  const [step, setStep] = useState<ImportStep>('source')
  const [held, setHeld] = useState<Held>({})
  const [reading, setReading] = useState(false)
  const [running, setRunning] = useState<'idle' | 'working' | 'failed'>('idle')
  const importShown = useBeat(running === 'working')
  const [dragging, setDragging] = useState(false)
  const [done, setDone] = useState<Done | null>(null)
  // What the last handing over had to say of the files that were none of the source's.
  const [refused, setRefused] = useState<string | null>(null)
  // The heading's back was pressed with files handed over, and is waiting on the answer.
  const [leavingBack, setLeavingBack] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const chosenSource = SOURCES.find((each) => each.id === source) ?? MACROFACTOR
  const files = MACROFACTOR_FILES.map((file) => ({
    ...file,
    handed: held[file.kind] !== undefined,
  }))
  const handedOver = files.some((file) => file.handed)
  const allIn = files.every((file) => file.handed)
  const busy = reading || running === 'working'

  /*
   * Files handed over are the only thing here that would be lost, and every way out but the
   * heading's back is a navigation — the card closing, a link elsewhere — so the guard sits
   * on the navigation, as the photo panel's does. Once the import has run there is nothing
   * left to lose.
   */
  const holding = useRef(false)
  useEffect(() => {
    holding.current = handedOver && done === null && running !== 'working'
  }, [handedOver, done, running])

  const leaving = useBlocker({
    shouldBlockFn: ({ next }) => holding.current && next.search.dialog !== 'import',
    enableBeforeUnload: false,
    withResolver: true,
  })

  /** Lets go of the files, back to the source step. */
  function abandon(): void {
    setHeld({})
    setRefused(null)
    setRunning('idle')
    setStep('source')
  }

  function backward(): void {
    if (busy) {
      return
    }
    if (step === 'files' && handedOver) {
      setLeavingBack(true)
    } else if (step === 'files') {
      abandon()
    } else if (step === 'how') {
      setStep('source')
    } else {
      onBack()
    }
  }

  /** The files picked, read one after another; each is told apart by what it holds. */
  async function handOver(picked: readonly File[]): Promise<void> {
    if (busy || picked.length === 0) {
      return
    }
    setReading(true)
    setRefused(null)
    let leftOut = 0
    let next = held
    try {
      for (const file of picked) {
        let kind: MacroFactorFile | null = null
        let workbook: Workbook | null = null
        try {
          workbook = readWorkbook(await bytesOf(file))
          kind = kindOf(workbook)
        } catch {
          kind = null
        }
        if (kind === null || workbook === null) {
          leftOut += 1
          continue
        }
        next = { ...next, [kind]: workbook }
        setHeld(next)
      }
      if (leftOut === 1) {
        setRefused(`One file is none of ${chosenSource.name} export's, and was left out.`)
      } else if (leftOut > 1) {
        setRefused(
          `${String(leftOut)} files are none of ${chosenSource.name} export's, and were left out.`,
        )
      }
    } finally {
      setReading(false)
    }
  }

  async function run(): Promise<void> {
    if (!allIn) {
      return
    }
    setRunning('working')
    try {
      const read = readMacroFactor(held)
      let profileFilled = 0
      for (const page of pagesOf(chosenSource.id, read)) {
        const written = await api.importData(page)
        profileFilled += written.profile.length
        if (written.profile.length > 0 && page.profile !== undefined) {
          // The stats the server filled in, so this device's copy of the settings says so too.
          const filled = Object.fromEntries(
            Object.entries(page.profile).filter(([name]) => written.profile.includes(name)),
          )
          await setSettings(filled).catch(() => undefined)
        }
      }
      holding.current = false
      // An import writes across the account, so what it wrote comes back with the feed.
      reloadNutrition()
      setDone({ reading: read, profileFilled })
      setStep('done')
      setRunning('idle')
    } catch {
      setRunning('failed')
    }
  }

  const stepAt = IMPORT_STEPS.indexOf(step) + 1
  const summary = done === null ? null : summaryOf(done.reading)

  return (
    <>
      <PanelHeader
        title="Import from another app"
        backLabel={step === 'files' ? 'Back to the source' : 'Back'}
        onBack={backward}
      />
      <StepLine at={stepAt} of={IMPORT_STEPS.length} title={IMPORT_STEP_TITLES[step]} />
      <div className="w-88 px-2 pb-2">
        {step === 'source' && (
          <>
            <fieldset>
              <legend className="sr-only">Source</legend>
              {SOURCES.map((each) => (
                <label
                  key={each.id}
                  className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-hover"
                >
                  <input
                    type="radio"
                    name="import-source"
                    aria-label={each.name}
                    className="mt-0.5 h-5 w-5 accent-accent"
                    checked={source === each.id}
                    onChange={() => {
                      setSource(each.id)
                    }}
                  />
                  {SOURCE_MARKS[each.id] !== undefined && (
                    <img
                      src={SOURCE_MARKS[each.id]}
                      alt={`${each.name} mark`}
                      className="mt-0.5 size-10 shrink-0 rounded-lg"
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{each.name}</span>
                    <span className="block text-sm text-ink-muted">
                      {`Its export is ${String(each.files.length)} files: the ${each.files
                        .map((file) => file.label.toLowerCase())
                        .join(' and the ')}.`}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="flex justify-end px-3 pt-2 pb-1">
              <button
                type="button"
                className={primary}
                onClick={() => {
                  setStep('how')
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
        {step === 'how' && (
          <div className="space-y-3 px-3 pb-2">
            <HowToTimeline steps={HOW_TO_EXPORT[chosenSource.id] ?? []} />
            <div className="flex justify-end">
              <button
                type="button"
                className={primary}
                onClick={() => {
                  setStep('files')
                }}
              >
                I have the files
              </button>
            </div>
          </div>
        )}
        {step === 'files' && (
          <div className="space-y-3 px-3 pb-2">
            <DropZone
              files={files.map((file) => ({
                kind: file.kind,
                label: file.label,
                state: file.handed ? 'in' : reading ? 'reading' : 'waiting',
              }))}
              sourceName={chosenSource.name}
              busy={reading}
              dragging={dragging}
              onAdd={() => {
                if (!busy) {
                  fileInput.current?.click()
                }
              }}
              onDragOver={(event) => {
                event.preventDefault()
                if (!busy) {
                  setDragging(true)
                }
              }}
              onDragLeave={() => {
                setDragging(false)
              }}
              onDrop={(event) => {
                event.preventDefault()
                setDragging(false)
                void handOver([...event.dataTransfer.files])
              }}
            />
            <input
              ref={fileInput}
              type="file"
              multiple
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-label="Export files"
              className="sr-only"
              onChange={(event) => {
                void handOver([...(event.target.files ?? [])])
                event.target.value = ''
              }}
            />
            {refused !== null && (
              <p role="alert" className="flex items-center gap-2 text-sm text-danger">
                <CloseIcon aria-hidden className="size-4 shrink-0" />
                {refused}
              </p>
            )}
            <p className="text-xs text-ink-muted">
              Importing adds to what you have logged here and never changes it. Importing the same
              export twice adds nothing.
            </p>
            {running === 'failed' && (
              <p role="alert" className="text-sm text-danger">
                The import could not run. Your files are still here — try again in a moment.
              </p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                aria-busy={running === 'working' || undefined}
                className={`relative ${primary}`}
                disabled={running !== 'working' && (!allIn || reading)}
                onClick={() => {
                  if (!busy) {
                    void run()
                  }
                }}
              >
                <Working shown={importShown}>
                  {running === 'failed' ? 'Try again' : 'Import'}
                </Working>
              </button>
            </div>
          </div>
        )}
        {step === 'done' && done !== null && summary !== null && (
          <div className="space-y-3 px-3 pb-2 text-sm">
            <p>
              {`${String(summary.entries)} entries over ${String(summary.days)} days` +
                (summary.from !== null && summary.to !== null
                  ? `, ${formatDayLabel(dayStart(summary.from))} to ${formatDayLabel(dayStart(summary.to))}.`
                  : '.')}
            </p>
            <p>
              {`${String(summary.foods)} foods of your own, ${String(summary.goals)} goals, ${String(summary.weighIns)} weigh-ins and ${String(summary.stepDays)} days of steps.`}
            </p>
            {done.profileFilled > 0 && (
              <p className="text-ink-muted">
                {`${String(done.profileFilled)} of your body stats were filled in from the export.`}
              </p>
            )}
            {summary.inProgress && (
              <div className="space-y-2 rounded-xl bg-surface p-3">
                <p>
                  {`Your goal in progress came across set by hand, with the targets ${chosenSource.name} coached. Set it up the coached way to have its weekly check-in here.`}
                </p>
                <Link
                  to="/you/goal"
                  search={(previous) => ({ ...withoutDialog(previous), way: 'coached' as const })}
                  className={`${primary} inline-block`}
                >
                  Set up the goal
                </Link>
              </div>
            )}
            {done.reading.skipped.recipesAsFoods.length > 0 && (
              <p className="text-ink-muted">
                {`${String(done.reading.skipped.recipesAsFoods.length)} recipes came across as foods: the export says nothing of their ingredients' nutrients.`}
              </p>
            )}
            {done.reading.skipped.foodsWithoutNutrients.length > 0 && (
              <p className="text-ink-muted">
                {`${String(done.reading.skipped.foodsWithoutNutrients.length)} foods were exported with no nutrients and got no food of their own; what you ate of them came across all the same.`}
              </p>
            )}
            {done.reading.skipped.rows > 0 && (
              <p className="text-ink-muted">
                {`${String(done.reading.skipped.rows)} rows named nothing, weighed nothing or said no calories, and were left out.`}
              </p>
            )}
            {done.reading.skipped.goalsWithoutProgram > 0 && (
              <p className="text-ink-muted">
                {`${String(done.reading.skipped.goalsWithoutProgram)} goals started before any program stood, so they had no targets to carry, and were left out.`}
              </p>
            )}
            <div className="flex justify-end">
              <button type="button" className={primary} onClick={onDone}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {(leavingBack || leaving.status === 'blocked') && (
        <ConfirmDialog
          title="Leave the import?"
          body="The files you have handed over will be let go of, and the import will not run."
          confirm="Leave"
          onConfirm={() => {
            holding.current = false
            abandon()
            if (leavingBack) {
              setLeavingBack(false)
            } else if (leaving.status === 'blocked') {
              leaving.proceed()
            }
          }}
          onDismiss={() => {
            if (leavingBack) {
              setLeavingBack(false)
            } else if (leaving.status === 'blocked') {
              leaving.reset()
            }
          }}
        />
      )}
    </>
  )
}

/** Where a file stands: not yet in, being read, or in and told apart. */
type FileState = 'waiting' | 'reading' | 'in'

/**
 * How the export is taken out of the source: a line down the left joining a dot per step,
 * and beside each the thing to do.
 */
function HowToTimeline({ steps }: { steps: readonly HowToStep[] }) {
  return (
    <ol aria-label="How to take the export" className="relative pl-7">
      <span aria-hidden className="absolute top-2 bottom-2 left-[7px] w-0.5 rounded bg-accent/30" />
      {steps.map((step, at) => (
        <li key={step.action} className={`relative ${at < steps.length - 1 ? 'pb-4' : ''}`}>
          <span
            aria-hidden
            className="absolute top-0.5 -left-7 grid size-4 place-items-center rounded-full bg-accent text-[10px] font-semibold text-accent-ink"
          >
            {at + 1}
          </span>
          <p className="text-sm font-medium">{step.action}</p>
          {step.detail !== undefined && <p className="text-xs text-ink-muted">{step.detail}</p>}
        </li>
      ))}
    </ol>
  )
}

/** A file's pill: its name behind a mark that says where it stands, and moves as it changes. */
function FilePill({ label, state }: { label: string; state: FileState }) {
  return (
    <li
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors duration-300 ${
        state === 'in' ? 'bg-ok/15 text-ink' : 'bg-surface-raised text-ink-muted'
      }`}
    >
      <span className="relative grid size-4 place-items-center">
        <CheckIcon
          aria-hidden
          className={`absolute size-4 transition-all duration-300 ${
            state === 'in'
              ? 'scale-100 text-ok opacity-100'
              : state === 'waiting'
                ? 'scale-100 opacity-30'
                : 'scale-50 opacity-0'
          }`}
        />
        <span
          aria-hidden
          className={`absolute size-3.5 animate-spin rounded-full border-2 border-accent/30 border-t-accent transition-opacity duration-300 ${
            state === 'reading' ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </span>
      {label}
      <span className="sr-only">
        {state === 'in' ? ', handed over' : state === 'reading' ? ', reading' : ', waiting'}
      </span>
    </li>
  )
}

/**
 * Where the files go: one big surface for all of them, a ring filling a segment per file as
 * each arrives, a count in the middle, and under the words the files themselves, each saying
 * where it stands. Several can be dropped at once, and whichever of the source's files each
 * turns out to be, it takes its own place.
 */
function DropZone({
  files,
  sourceName,
  busy,
  dragging,
  onAdd,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  files: readonly { kind: string; label: string; state: FileState }[]
  sourceName: string
  busy: boolean
  dragging: boolean
  onAdd: () => void
  onDragOver: (event: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (event: React.DragEvent) => void
}) {
  const radius = 34
  const around = 2 * Math.PI * radius
  const gap = 6
  const of = files.length
  const handed = files.filter((file) => file.state === 'in').length
  const busyShown = useBeat(busy)
  const segment = of === 0 ? around : around / of - gap
  return (
    <div
      className={`flex w-full flex-col items-center gap-4 rounded-3xl border-2 border-dashed px-4 pt-4 pb-4 text-sm transition-colors ${
        dragging
          ? 'border-accent bg-hover text-ink'
          : handed === of && of > 0
            ? 'border-ok/60 text-ink'
            : 'border-white/15 text-ink-muted'
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <button
        type="button"
        aria-busy={busy || undefined}
        aria-label="Add your files"
        className="flex w-full flex-col items-center gap-2 rounded-2xl px-4 py-2 transition hover:bg-hover"
        onClick={onAdd}
      >
        <span className="relative grid size-20 place-items-center">
          <svg aria-hidden viewBox="0 0 80 80" className="absolute inset-0 -rotate-90 size-20">
            {Array.from({ length: Math.max(of, 1) }, (_, at) => (
              <circle
                key={at}
                cx="40"
                cy="40"
                r={radius}
                fill="none"
                strokeWidth="6"
                className={`transition-colors duration-300 ${at < handed ? 'stroke-ok' : 'stroke-white/15'}`}
                strokeDasharray={`${String(segment)} ${String(around - segment)}`}
                strokeDashoffset={String(-at * (segment + gap))}
              />
            ))}
          </svg>
          {/* Working, the count in the middle of the ring gives way to the spinner. */}
          <Working shown={busyShown} spinner="size-6 text-ink">
            <span className="font-medium text-ink">{`${String(handed)} / ${String(of)}`}</span>
          </Working>
        </span>
        <span className="font-medium text-ink">Add your files</span>
        <span className="text-xs">
          {`Drop your ${sourceName} files here, one or several at once, or click to browse.`}
        </span>
      </button>
      <ul aria-label="Files" className="flex flex-wrap justify-center gap-2">
        {files.map((file) => (
          <FilePill key={file.kind} label={file.label} state={file.state} />
        ))}
      </ul>
    </div>
  )
}
